import { useMemo } from 'react';
import type { Booking } from '@api/types';
import { STATUS_MAP } from './BookingDetailDrawer';
import { fmtTime, MONTHS_ES_SHORT, DOW_ES_LONG, bookingTone } from './calendar-shared';

interface CalendarAgendaProps {
  bookings: readonly Booking[];
  onOpenBooking: (id: string) => void;
}

interface AgendaRow {
  date: string; // ISO yyyy-mm-dd
  dow: string;
  day: number;
  month: string;
  bookings: Booking[];
}

export function CalendarAgenda({ bookings, onOpenBooking }: CalendarAgendaProps) {
  const rows = useMemo<AgendaRow[]>(() => {
    const byDay: Record<string, Booking[]> = {};
    for (const b of bookings) {
      const day = b.startTime.slice(0, 10);
      (byDay[day] ??= []).push(b);
    }
    const dates = Object.keys(byDay).sort();
    return dates.map((iso) => {
      const list = byDay[iso] ?? [];
      list.sort((a, b) => a.startTime.localeCompare(b.startTime));
      const [yStr, mStr, dStr] = iso.split('-');
      const y = Number(yStr);
      const m = Number(mStr);
      const d = Number(dStr);
      const date = new Date(y, m - 1, d);
      return {
        date: iso,
        dow: DOW_ES_LONG[(date.getDay() + 6) % 7] ?? '',
        day: d,
        month: MONTHS_ES_SHORT[m - 1] ?? '',
        bookings: list,
      };
    });
  }, [bookings]);

  if (rows.length === 0) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--fg-muted)' }}>
        Sin reservas en el rango de la agenda.
      </div>
    );
  }

  return (
    <div className="cal-agenda">
      {rows.map((row) => (
        <section key={row.date} className="cal-agenda-day">
          <div className="cal-agenda-date">
            <span className="cal-agenda-d mono">{row.day}</span>
            <span className="cal-agenda-m mono">{row.month}</span>
            <span className="cal-agenda-dow">{row.dow}</span>
          </div>
          <div className="cal-agenda-list">
            {row.bookings.map((b) => (
              <button
                key={b.id}
                type="button"
                className="cal-agenda-row"
                onClick={() => { onOpenBooking(b.id); }}
              >
                <span className="cal-agenda-time mono">{fmtTime(b.startTime)}</span>
                <span className="cal-agenda-color" data-tone={bookingTone(b.status)} />
                <div className="cal-agenda-body">
                  <div className="cal-agenda-svc">{b.serviceName}</div>
                  <div className="cal-agenda-meta">
                    <span>{b.customerName}</span>
                    {b.staffName && (<><span className="dot" /><span>{b.staffName}</span></>)}
                    {b.roomName && (<><span className="dot" /><span>{b.roomName}</span></>)}
                  </div>
                </div>
                <span className="pill" data-tone={STATUS_MAP[b.status].tone}>{STATUS_MAP[b.status].label}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
